import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Notification from '../components/common/Notification';

// Create the context
const NotificationContext = createContext();

/**
 * Custom hook to use the notification context
 * @returns {Object} Notification methods
 */
export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Notification provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  // Clean up expired notifications periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setNotifications(prev => 
        prev.filter(notification => 
          !notification.expiresAt || notification.expiresAt > now
        )
      );
    }, 1000);

    return () => clearInterval(cleanupInterval);
  }, []);

  // Add a notification
  const addNotification = useCallback((message, type = 'info', autoClose = true, duration = 5000) => {
    // Generate a unique ID based on the message content and type
    const id = `${type}-${message}`.replace(/\s+/g, '-').toLowerCase();
    const expiresAt = autoClose ? Date.now() + duration : null;
    
    setNotifications(prev => {
      // Filter out any existing notifications with the same ID
      const filteredNotifications = prev.filter(notification => notification.id !== id);
      
      // Add the new notification
      return [
        ...filteredNotifications,
        {
          id,
          message,
          type,
          autoClose,
          duration,
          expiresAt
        }
      ];
    });
    
    return id;
  }, []);

  // Remove a notification
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  // Shorthand methods for different notification types
  const showSuccess = useCallback((message, autoClose = true, duration = 3000) => {
    return addNotification(message, 'success', autoClose, duration);
  }, [addNotification]);

  const showError = useCallback((message, autoClose = true, duration = 5000) => {
    return addNotification(message, 'error', autoClose, duration);
  }, [addNotification]);

  const showWarning = useCallback((message, autoClose = true, duration = 4000) => {
    return addNotification(message, 'warning', autoClose, duration);
  }, [addNotification]);

  const showInfo = useCallback((message, autoClose = true, duration = 3000) => {
    return addNotification(message, 'info', autoClose, duration);
  }, [addNotification]);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Context value
  const value = {
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clearAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      
      {/* Render all notifications */}
      <div className="notification-container fixed right-4 bottom-4 z-50 space-y-2 flex flex-col-reverse items-end">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            type={notification.type}
            message={notification.message}
            autoClose={notification.autoClose}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export default NotificationContext;